import { useEffect, useState } from 'react';
import { useAlert } from '@weave/alert-system';
import { HandoffMetric } from 'components/onboarding/handoff-hub/handoff-hub.types';
import {
  UnifyLocationDetails,
  UnifyLocationRow,
} from '../components/unify-locations-table/unify-locations-table.types';
import { useGetHandoffSnapshotsByGroupIDQuery } from '../queries/unify-handoff.queries';

export interface UseUnifyDraftRehydrationProps {
  initialGroupId?: string;
  initialHandoffIdMap?: Record<string, string>;
  onRehydrateSelection?: (locationIds: string[]) => void;
  onRehydrateDetails?: (detailsMap: Record<string, UnifyLocationDetails>) => void;
}

export interface UseUnifyDraftRehydrationReturnValues {
  groupId?: string;
  setGroupId: React.Dispatch<React.SetStateAction<string | undefined>>;
  handoffIdMap: Record<string, string>;
  setHandoffIdMap: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  snapshotMetricsByLocation: Record<string, HandoffMetric[]>;
  setSnapshotMetricsByLocation: React.Dispatch<
    React.SetStateAction<Record<string, HandoffMetric[]>>
  >;
  isLoadingDraft: boolean;
}

export const useUnifyDraftRehydration = ({
  initialGroupId,
  initialHandoffIdMap = {},
  onRehydrateSelection,
  onRehydrateDetails,
}: UseUnifyDraftRehydrationProps): UseUnifyDraftRehydrationReturnValues => {
  const alert = useAlert();
  const [groupId, setGroupId] = useState<string | undefined>(initialGroupId);
  const [handoffIdMap, setHandoffIdMap] =
    useState<Record<string, string>>(initialHandoffIdMap);
  const [snapshotMetricsByLocation, setSnapshotMetricsByLocation] = useState<
    Record<string, HandoffMetric[]>
  >({});

  const {
    data: groupSnapshotsData,
    isLoading: isLoadingDraft,
    isError: isGroupSnapshotsError,
  } = useGetHandoffSnapshotsByGroupIDQuery(
    { groupId: initialGroupId },
    { enabled: !!initialGroupId }
  );

  useEffect(() => {
    if (isGroupSnapshotsError) {
      alert.error('Error fetching handoff draft details for this group');
    }
  }, [isGroupSnapshotsError, alert]);

  useEffect(() => {
    if (
      !groupSnapshotsData?.handoffSnapshots ||
      groupSnapshotsData.handoffSnapshots.length === 0
    ) {
      return;
    }

    const snapshots = groupSnapshotsData.handoffSnapshots;
    const rehydratedLocationIds: string[] = [];
    const rehydratedIdMap: Record<string, string> = {};
    const rehydratedDetailsMap: Record<string, UnifyLocationDetails> = {};
    const rehydratedMetricsMap: Record<string, HandoffMetric[]> = {};

    snapshots.forEach((snap) => {
      if (snap.locationId) {
        rehydratedLocationIds.push(snap.locationId);
        if (snap.id) {
          rehydratedIdMap[snap.locationId] = snap.id;
        }
        rehydratedDetailsMap[snap.locationId] = {
          pocEmail: snap.pointOfContact || '',
          notes: {
            officeExperience: snap.officeExperience || '',
            disclaimerTypeSent: snap.disclaimerTypeSent || '',
            routerMakeAndModel: snap.routerMakeAndModel || '',
          },
        };
        if (snap.metrics) {
          rehydratedMetricsMap[snap.locationId] = snap.metrics as HandoffMetric[];
        }
      }
    });

    onRehydrateSelection?.(rehydratedLocationIds);
    onRehydrateDetails?.(rehydratedDetailsMap);
    setHandoffIdMap(rehydratedIdMap);
    setSnapshotMetricsByLocation(rehydratedMetricsMap);
    if (initialGroupId) {
      setGroupId(initialGroupId);
    }
  }, [groupSnapshotsData, initialGroupId, onRehydrateSelection, onRehydrateDetails]);

  return {
    groupId,
    setGroupId,
    handoffIdMap,
    setHandoffIdMap,
    snapshotMetricsByLocation,
    setSnapshotMetricsByLocation,
    isLoadingDraft,
  };
};
